/* eslint-disable import/no-unused-modules */
import type {
    Proposal,
    ProductItem,
    PaymentItem,
    ProfessionalInfo,
    Contact,
    ProposalTemplate,
    InvoicePaymentStatus,
    Currency,
    InvoiceStatus,
} from '../type/types';
import type { InvoiceColumnProps } from '~/modules/page-builder/component/edit/invoice-module/types';

/**
 * Maps InvoiceModule column data (from page builder modules) to Proposal interface
 * @param columnData - The invoice column data from a page builder module
 * @param urlSegment - The url segment of the proposal
 * @param professionalData - Professional data from the module meta
 * @returns Proposal object that matches the expected interface
 */
export const mapInvoiceModuleToProposal = (
    columnData: InvoiceColumnProps,
    urlSegment: string,
    professionalData?: {
        id: number;
        title: string;
    }
): Proposal => {
    if (!columnData) {
        throw new Error('Invalid invoice column data');
    }

    // Determine currency from services or default to NZD
    const currency: Currency = 'NZD'; // Default, could be extracted from services if needed

    // Map professional info (minimal data available from column)
    const professionalInfo: ProfessionalInfo = {
        title: professionalData?.title || '',
        subTitle: '',
        address: '',
        city: '',
        postCode: '',
        state: '',
        bn: '',
    };

    // Map contact info
    const contact: Contact = {
        id: columnData.contact?.id ? parseInt(columnData.contact.id) : 0,
        fullName: columnData.contact?.name || '',
        email: columnData.contact?.email || '',
        firstName: columnData.contact?.firstName || '',
        lastName: columnData.contact?.surname || '',
        phone: columnData.contact?.phone || '',
        address: columnData.contact?.address || '',
        postCode: columnData.contact?.postCode || '',
        city: columnData.contact?.city || '',
        hasStripePaymentMethod: false,
    };

    // Map proposal template with tokenized fields
    const proposalTemplate: ProposalTemplate = {
        headline: columnData.headline || '',
        subHeadline: columnData.subheadline || '',
        footerLinks: [],
        note: columnData.notes?.Render ? [columnData.notes.Render] : [],
        contact: contact,
        accountDetails: columnData.accountDetails?.Render,
        paymentTerms: columnData.paymentTerms?.Render,
        creditCardTerms: columnData.creditCardTerms?.Render,
        bankTransferTerms: columnData.bankTransferTerms?.Render,
        references: columnData.references?.Render,
        termsOfService: columnData.termsOfService?.Render,
        termsOfServiceTitle: columnData.termsOfServiceTitle,
        scope: columnData.scope?.Render,
    };

    // Map product items from services
    const productItems: ProductItem[] =
        columnData.services?.map((service) => ({
            id: service.id ? parseInt(service.id) : 0,
            name: service.title,
            description: service.description || '',
            imageUrl:
                service.photoSrc ||
                '/assets/website/ecommerce/images/proposal-product-demo1-60x60.png',
            quantity: service.quantity || 0,
            unitPrice: service.unitPrice || 0,
            discountedUnitPrice: service.discountedUnitPrice || 0,
            discountApplied: service.discountApplied,
            taxRate: service.taxRate || 0,
            totalPrice:
                (service.discountApplied
                    ? service.discountedUnitPrice || 0
                    : service.unitPrice || 0) * (service.quantity || 0),
            currency,
        })) || [];

    // Map payment items
    const paymentItems: PaymentItem[] =
        columnData.payments?.map((payment, idx) => ({
            id: payment.id ? parseInt(payment.id) : 0,
            name: `Payment ${idx + 1}`,
            status: mapPaymentStatus(payment.status),
            dueDate: payment.dueDate
                ? new Date(payment.dueDate).getTime()
                : null,
            paymentDate: payment.paymentDate
                ? new Date(payment.paymentDate).getTime()
                : null,
            amount: payment.amount || 0,
            currency,
        })) || [];

    // Use financial totals from backend (already calculated correctly)
    const subtotal = columnData.subtotal ?? 0;
    const discounts = columnData.discounts ?? 0;
    const tax = columnData.tax ?? 0;
    const total = columnData.total ?? 0;

    // Map summaries from scope field
    const summaries: string[] = columnData.scope?.Render
        ? columnData.scope.Render.split('\n')
        : [];

    // Default values for fields not available in column data
    const status = columnData.status as InvoiceStatus;
    const docType: 'quote' | 'invoice' = [
        'Draft',
        'Sent',
        'Expired',
        'Cancelled',
    ].includes(status)
        ? 'quote'
        : 'invoice';
    const isSubscription = paymentItems.length > 1;

    return {
        id: parseInt(columnData.id) || 0,
        urlSegment: urlSegment,
        uuid: urlSegment, // Use urlSegment as UUID fallback
        docType,
        isSubscription,
        professionalInfo,
        proposalTemplate,
        productItems,
        paymentItems,
        status,
        currency,
        discounts,
        subtotal,
        tax,
        total,
        summaries,
        customer: contact,
        expiryDate: columnData.expiryDate
            ? new Date(columnData.expiryDate).getTime()
            : null,
        invoiceSentDate: columnData.invoiceSentDate
            ? new Date(columnData.invoiceSentDate).getTime()
            : null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        stripeHostedInvoiceUrl: null,
        stripeAccount: columnData.stripeAccount || null,
        hasStripePaymentMethod: columnData.hasStripePaymentMethod ?? false,
        poNumber: columnData.poNumber,
        startDate: columnData.startDate,
        endDate: columnData.endDate,
    };
};

/**
 * Maps payment status from page builder to component interface
 * @param status - Payment status from page builder
 * @returns Mapped payment status
 */
const mapPaymentStatus = (
    status: string | null | undefined
): InvoicePaymentStatus => {
    if (!status) return 'Upcoming';

    switch (status.toLowerCase()) {
        case 'fullypaid':
            return 'FullyPaid';
        case 'pending':
        case 'upcoming':
            return 'Upcoming';
        case 'overdue':
            return 'Overdue';
        case 'canceled':
            return 'Canceled';
        default:
            return 'Upcoming';
    }
};
